"use client";

// The recovery body: `<RecoverFunds>` (the very same panel the deposit wizard's
// gear opens) hosted directly in the shared modal shell. The visible title/X
// are intentionally absent; ShellContent keeps the accessible sr-only title
// and the shell owns Escape/outside-click/swipe dismissal. The old
// delivery-token picker is gone (upstream e5bff75): the panel now runs in
// per-forwarder mode and lists EVERY forwarder holding funds at once, so there
// is no "which target to inspect" left to pick.
//
// Sibling of RelayHistoryContent.tsx: same wiring shape (read every prop off
// the compound context, resolve `client` from `client` > `apiBaseUrl` >
// `environment` via resolveRelayApiBaseUrl, wrap in TrackingBoundary +
// ShellContent), and the same "no request before the modal opens" property for
// free -- this subtree lives inside radix `Dialog.Portal` / vaul
// `Drawer.Portal`, which render nothing while closed, and unmounts on close.
//
// Two differences from the history modal, both intrinsic to recovery:
//
//   * it needs INJECTED WALLET CAPABILITIES. Recovery is signing-first (the
//     user pays BSC gas for a permissionless forwarder deploy/sweep), so a
//     wallet missing the writeContract/readContract/waitForReceipt trio that
//     useRecovery.recover() guards on gets a one-line note instead of a panel
//     whose only button could never work;
//   * it consumes the root's shared config/recovery snapshot. The trigger needs
//     that snapshot while closed, so fetching it again here would multiply API
//     calls on every close/reopen cycle.
import * as React from "react";

import { TrackingBoundary, trackDappUiFunnel } from "../../../lib/sentry/index";
import { resolveEnvironment } from "../../../lib/environment";
import {
  useRemoteConfigValue,
  type AppsConfig,
  resolveRelayForwarderFactories,
} from "../../../lib/remote-config";
import { useRelayOrders } from "../hooks/useRelayOrders";
import { resolveRelayApiBaseUrl } from "../lib/api-base";
import { mergeTrustedFactories } from "../lib/verifyForwarder";
import { createRelayClient } from "../lib/client";
import { isAddress } from "../lib/units";
import {
  useRelayRecoveryContext,
  useRelayRecoverySnapshot,
} from "../providers/relay-recovery-provider";
import { useRelayShellContext } from "../providers/relay-shell-provider";
import { RecoverFunds } from "./RecoverFunds";
import { ShellContent } from "./shell/ResponsiveShell";
import type { RelayWalletProps } from "../types";
import "../styles/component.css";

export interface RelayRecoveryContentProps {
  /** Extra class name(s) for the dialog/drawer content element. Falls back to
   * `<RelayRecovery>`'s own `className`. */
  className?: string;
}

/**
 * `<RelayRecovery.Content>` — the modal/drawer surface. Same portal-scoped
 * lifecycle as `<RelayHistory.Content>`: nothing is requested before the modal
 * is opened, and the whole subtree (including `useRecovery`'s state and any
 * in-flight recovery tracking) unmounts on close, so there is no per-open state
 * to reset explicitly.
 */
export function RelayRecoveryContent({ className }: RelayRecoveryContentProps) {
  const props = useRelayRecoveryContext();
  const { open } = useRelayShellContext();

  React.useEffect(() => {
    if (open) trackDappUiFunnel("relay", "recovery_opened");
  }, [open]);

  return (
    <TrackingBoundary feature="relay">
      <ShellContent ariaTitle="Recover stuck funds" className={className ?? props.className}>
        <RelayRecoveryBody />
      </ShellContent>
    </TrackingBoundary>
  );
}

function RelayRecoveryBody() {
  const props = useRelayRecoveryContext();
  const {
    targets: recoveryTargets,
    loading: recoveryLoading,
    refresh: refreshRecovery,
  } = useRelayRecoverySnapshot();
  const { theme } = useRelayShellContext();

  // Resolution order: `client` (already built, needs no URL at all) >
  // `apiBaseUrl` > `environment`'s built-in default map. Resolved once and
  // threaded into BOTH the catalog client below and <RecoverFunds> (whose own
  // useRecovery builds a third client from it), so all three agree.
  const resolvedApiBaseUrl = React.useMemo(
    () => resolveRelayApiBaseUrl({ apiBaseUrl: props.apiBaseUrl, environment: props.environment }),
    [props.apiBaseUrl, props.environment],
  );

  const client = React.useMemo(() => {
    if (props.client) return props.client;
    return createRelayClient({ baseUrl: resolvedApiBaseUrl });
  }, [props.client, resolvedApiBaseUrl]);

  // The injected capabilities, re-bundled into the shape <RecoverFunds> ->
  // useRecovery takes. Memoized on the individual functions (same rationale as
  // RelayWizardStateHost's own `wallet` memo) so a host passing stable
  // references doesn't hand useRecovery a fresh object every render.
  const wallet = React.useMemo<RelayWalletProps>(
    () => ({
      walletAddress: props.walletAddress,
      walletChainId: props.walletChainId,
      sendTransaction: props.sendTransaction,
      writeContract: props.writeContract,
      readContract: props.readContract,
      switchChain: props.switchChain,
      waitForReceipt: props.waitForReceipt,
      getBalance: props.getBalance,
    }),
    [
      props.walletAddress,
      props.walletChainId,
      props.sendTransaction,
      props.writeContract,
      props.readContract,
      props.switchChain,
      props.waitForReceipt,
      props.getBalance,
    ],
  );

  const connectedWallet =
    props.walletAddress && isAddress(props.walletAddress)
      ? props.walletAddress
      : undefined;
  const recipientMatchesWallet = Boolean(
    connectedWallet &&
      (!props.recipient ||
        (isAddress(props.recipient) &&
          props.recipient.toLowerCase() === connectedWallet.toLowerCase())),
  );
  const resolvedRecipient = recipientMatchesWallet ? connectedWallet : undefined;

  // Same CDN resolution the deposit wizard applies (see RelayWizardStateHost):
  // Only role-labelled `forwarderFactories` is read by current code. The
  // legacy `trustedFactories` compatibility array is deliberately ignored.
  // The resolved list REPLACES the pinned defaults; an explicit host prop wins.
  const resolvedEnv = resolveEnvironment(props.environment);
  const remoteTrusted = useRemoteConfigValue<AppsConfig, string[] | undefined>(
    "apps.json",
    resolvedEnv,
    (config) => resolveRelayForwarderFactories(config, resolvedEnv),
  );
  const effectiveTrustedFactories = React.useMemo(
    () => props.trustedFactories ?? mergeTrustedFactories(remoteTrusted),
    [props.trustedFactories, remoteTrusted],
  );

  // The exact trio useRecovery.recover() guards on: missing any one of them
  // means every button in the panel would throw on click, so the panel is not
  // rendered at all. `getBalance`/`sendTransaction` are irrelevant here --
  // recovery never reads a balance or does a plain transfer.
  const canSign = Boolean(props.writeContract && props.readContract && props.waitForReceipt);
  const ready = Boolean(resolvedRecipient) && canSign;
  const recipientKey = resolvedRecipient;

  // Read-only provenance for the balance-composition rows -- which deposits
  // make up each forwarder's balance (BSC_FUNDED orders only; see
  // ../lib/recoveryGroups.ts).
  const { orders } = useRelayOrders({
    client,
    recipient: ready ? recipientKey : undefined,
    enabled: ready,
    // Orders only annotate the current recovery balance. One snapshot is
    // enough; opening an SSE connection plus its on-open refetch here doubled
    // API traffic on every modal reopen.
    live: false,
  });

  return (
    <div className="rd-root rd-wizard" data-theme={theme}>
      {!connectedWallet && (
        <p className="rd-muted" data-testid="rd-recovery-connect-note">
          Connect a wallet to check recoverable funds.
        </p>
      )}

      {connectedWallet && !recipientMatchesWallet && (
        <p className="rd-muted" data-testid="rd-recovery-recipient-note">
          Recovery is only available for the connected wallet.
        </p>
      )}

      {recipientMatchesWallet && !canSign && (
        <p className="rd-muted" data-testid="rd-recovery-unsupported-note">
          This wallet can&apos;t sign recovery transactions.
        </p>
      )}

      {ready && (
        <RecoverFunds
          client={props.client}
          apiBaseUrl={resolvedApiBaseUrl}
          recipient={resolvedRecipient}
          wallet={wallet}
          trustedFactories={effectiveTrustedFactories}
          // Always supply the root snapshot, including its empty/loading state,
          // so RecoverFunds never starts a duplicate single-target request.
          targets={recoveryTargets}
          targetsLoading={recoveryLoading}
          orders={orders}
          onRecovered={refreshRecovery}
          onRecoverySuccess={props.onRecoverySuccess}
          // ShellContent owns the screen-reader-only modal title.
          showTitle={false}
          onError={props.onError}
        />
      )}
    </div>
  );
}
